import {
  mcpResourceScopeSelectionSchema,
  type McpResource,
} from "@arti/schemas/mcpResources";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type DashboardMcpGrant,
  type DashboardMcpToken,
  type DashboardMcpTokenCreateResponse,
  createMcpAccessTokenAsUser,
  listConnectedMcpOauthGrantsAsUser,
  listMcpAccessTokensAsUser,
  revokeMcpAccessTokenAsUser,
  revokeMcpOauthGrantAsUser,
} from "../dashboard-client";

interface UseMcpTokensState {
  enabled: boolean | null;
  error: string | null;
  grants: DashboardMcpGrant[];
  loading: boolean;
  tokens: DashboardMcpToken[];
}

export function useMcpTokens(
  organizationId: string | null,
  resource: McpResource = "arti",
) {
  const requestSequence = useRef(0);
  const [state, setState] = useState<UseMcpTokensState>({
    enabled: null,
    error: null,
    grants: [],
    loading: false,
    tokens: [],
  });

  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;
    if (!organizationId) {
      setState({
        enabled: null,
        error: null,
        grants: [],
        loading: false,
        tokens: [],
      });
      return;
    }

    setState((current) => ({ ...current, error: null, loading: true }));
    try {
      const [{ data: grantsData }, { data: tokensData }] = await Promise.all([
        listConnectedMcpOauthGrantsAsUser({ query: { resource } }),
        listMcpAccessTokensAsUser({ query: { resource } }),
      ]);
      if (sequence !== requestSequence.current) {
        return;
      }
      setState({
        enabled: tokensData.enabled,
        error: null,
        grants: grantsData.items,
        loading: false,
        tokens: tokensData.items,
      });
    } catch (error) {
      if (sequence !== requestSequence.current) {
        return;
      }
      setState((current) => ({
        ...current,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load MCP access data",
        loading: false,
      }));
    }
  }, [organizationId, resource]);

  useEffect(() => {
    void refresh();
    return () => {
      requestSequence.current += 1;
    };
  }, [refresh]);

  const createToken = useCallback(
    async (label: string, scopes: string[]) => {
      const normalizedLabel = label.trim();
      const selection = mcpResourceScopeSelectionSchema.parse({
        resource,
        scopes,
      });
      const { data } = await createMcpAccessTokenAsUser({
        body: {
          ...(normalizedLabel ? { label: normalizedLabel } : {}),
          ...selection,
        },
      });
      await refresh();
      return data satisfies DashboardMcpTokenCreateResponse;
    },
    [refresh, resource],
  );

  const revokeToken = useCallback(
    async (tokenId: string) => {
      await revokeMcpAccessTokenAsUser({
        path: { tokenId },
        query: { resource },
      });
      await refresh();
    },
    [refresh, resource],
  );

  const revokeGrant = useCallback(
    async (grantId: string) => {
      await revokeMcpOauthGrantAsUser({
        path: { grantId },
        query: { resource },
      });
      await refresh();
    },
    [refresh, resource],
  );

  return {
    createToken,
    enabled: state.enabled,
    error: state.error,
    grants: state.grants,
    loading: state.loading,
    refresh,
    revokeGrant,
    revokeToken,
    tokens: state.tokens,
  };
}
